/**
 * Resumable video upload manager — US-A04, docs/03 §5.1.
 *
 * Why a module-level singleton and not component state: AC3 requires the upload
 * to continue when the content manager navigates elsewhere in admin. A `tus`
 * upload owned by a React component dies with its unmount. Owned here, the
 * panel can unmount and remount and simply re-subscribe.
 *
 * Flow (docs/03 §5.1):
 *   admin → edge fn `media-create-upload` (service role; creates the Bunny
 *   Stream video and returns its GUID plus a scoped TUS endpoint)
 *   → browser PUTs the bytes straight to Bunny over TUS, chunked and resumable
 *   → Bunny encodes → Bunny webhook → edge fn updates content_items.video_status
 *   → this panel sees Processing → Ready/Failed over a realtime subscription.
 *
 * Bytes never touch our backend, so a 500 MB file costs no backend compute and
 * is not bounded by a request timeout.
 */
import * as tus from 'tus-js-client'
import { invokeEdgeFunction } from '@/lib/supabase'
import { t } from '@/providers/i18n-provider'
import type { VideoStatus } from '@/types'

/** Formats admin accepts. AC7: anything else is refused before a byte moves. */
export const ACCEPTED_VIDEO_MIME = ['video/mp4', 'video/quicktime', 'video/x-matroska']
export const ACCEPTED_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.mkv']
/** Chunk size: large enough to be efficient, small enough to resume cheaply. */
const CHUNK_SIZE = 8 * 1024 * 1024
const RETRY_DELAYS = [0, 3000, 10000, 30000, 60000]

export type UploadPhase = 'idle' | 'creating' | 'uploading' | 'paused' | 'uploaded' | 'error'

export interface UploadState {
  contentId: string
  phase: UploadPhase
  fileName: string
  bytesSent: number
  bytesTotal: number
  /** 0–100. */
  percent: number
  /** Bytes per second, smoothed. */
  speed: number
  /** Seconds remaining, null while unknown. */
  etaSeconds: number | null
  videoId: string | null
  error: string | null
}

/**
 * Response of the `media-create-upload` edge function.
 *
 * TODO(US-A04): the contract is owned by the backend agent; docs/03 §5.1
 * specifies the behaviour ("creates a Bunny Stream video, returns its GUID + a
 * scoped TUS upload URL") but not the field names. These are the working names
 * for this scaffold — one place to change when the function lands.
 */
export interface CreateUploadResponse {
  videoId: string
  uploadUrl: string
  /** Signed headers Bunny requires on the TUS request. */
  headers: Record<string, string>
  /** Unix seconds; the signature expires with it. */
  expiresAt: number
}

export interface StartUploadOptions {
  contentId: string
  file: File
  /** Sent to the edge function so the created video is named usefully in Bunny. */
  title: string
}

type Listener = (state: UploadState) => void

export function validateVideoFile(file: File): string | null {
  const lower = file.name.toLowerCase()
  const extensionOk = ACCEPTED_VIDEO_EXTENSIONS.some(extension => lower.endsWith(extension))
  const mimeOk = file.type === '' || ACCEPTED_VIDEO_MIME.includes(file.type)
  if (!extensionOk || !mimeOk) {
    return t('admin.upload.unsupportedFormat', { formats: ACCEPTED_VIDEO_EXTENSIONS.join(', ') })
  }
  if (file.size === 0) return t('admin.upload.emptyFile')
  return null
}

export function formatEta(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—'
  if (seconds < 60) return t('admin.units.seconds', { value: Math.ceil(seconds) })
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60)
    return t('admin.units.minutesSeconds', { minutes, seconds: Math.round(seconds % 60) })
  return t('admin.units.hoursMinutes', { hours: Math.floor(minutes / 60), minutes: minutes % 60 })
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return t('admin.units.bytes', { value: bytes })
  const units = ['admin.units.kilobytes', 'admin.units.megabytes', 'admin.units.gigabytes']
  let value = bytes / 1024
  let index = 0
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }
  return t(units[index] as string, { value: value.toFixed(1) })
}

interface Entry {
  upload: tus.Upload | null
  state: UploadState
  /** Kept so a retry after an encoding failure never re-reads a missing file. */
  file: File | null
  startedAt: number
  listeners: Set<Listener>
}

class UploadManager {
  private entries = new Map<string, Entry>()

  getState(contentId: string): UploadState | undefined {
    return this.entries.get(contentId)?.state
  }

  subscribe(contentId: string, listener: Listener): () => void {
    const entry = this.ensure(contentId)
    entry.listeners.add(listener)
    listener(entry.state)
    return () => {
      entry.listeners.delete(listener)
    }
  }

  private ensure(contentId: string): Entry {
    let entry = this.entries.get(contentId)
    if (!entry) {
      entry = {
        upload: null,
        file: null,
        startedAt: 0,
        listeners: new Set(),
        state: {
          contentId,
          phase: 'idle',
          fileName: '',
          bytesSent: 0,
          bytesTotal: 0,
          percent: 0,
          speed: 0,
          etaSeconds: null,
          videoId: null,
          error: null,
        },
      }
      this.entries.set(contentId, entry)
    }
    return entry
  }

  private emit(contentId: string, patch: Partial<UploadState>): void {
    const entry = this.ensure(contentId)
    entry.state = { ...entry.state, ...patch }
    for (const listener of entry.listeners) listener(entry.state)
  }

  async start({ contentId, file, title }: StartUploadOptions): Promise<void> {
    const invalid = validateVideoFile(file)
    if (invalid) {
      this.emit(contentId, { phase: 'error', error: invalid })
      throw new Error(invalid)
    }

    const entry = this.ensure(contentId)
    entry.file = file
    entry.startedAt = Date.now()

    this.emit(contentId, {
      phase: 'creating',
      fileName: file.name,
      bytesTotal: file.size,
      bytesSent: 0,
      percent: 0,
      error: null,
    })

    let created: CreateUploadResponse
    try {
      created = await invokeEdgeFunction<CreateUploadResponse>('media-create-upload', {
        content_id: contentId,
        file_name: file.name,
        file_size: file.size,
        title,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : t('admin.upload.createFailed')
      this.emit(contentId, { phase: 'error', error: message })
      throw error
    }

    this.emit(contentId, { videoId: created.videoId, phase: 'uploading' })

    const upload = new tus.Upload(file, {
      endpoint: created.uploadUrl,
      chunkSize: CHUNK_SIZE,
      retryDelays: RETRY_DELAYS,
      // AC2: the fingerprint keys the resume record in localStorage, so a page
      // reload or a dropped connection resumes instead of restarting.
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      fingerprint: async () => `horizon-${contentId}-${file.name}-${file.size}`,
      headers: created.headers,
      metadata: {
        filename: file.name,
        filetype: file.type,
        contentId,
      },
      onProgress: (bytesSent, bytesTotal) => {
        const elapsed = (Date.now() - entry.startedAt) / 1000
        const speed = elapsed > 0 ? bytesSent / elapsed : 0
        const remaining = bytesTotal - bytesSent
        this.emit(contentId, {
          phase: 'uploading',
          bytesSent,
          bytesTotal,
          percent: bytesTotal > 0 ? Math.round((bytesSent / bytesTotal) * 100) : 0,
          speed,
          etaSeconds: speed > 0 ? remaining / speed : null,
        })
      },
      onSuccess: () => {
        // Encoding is asynchronous. video_status moves to `processing` and then
        // `ready` or `failed` via the Bunny webhook (docs/03 §5.1); the panel
        // watches the row, not this callback.
        this.emit(contentId, {
          phase: 'uploaded',
          percent: 100,
          etaSeconds: 0,
        })
      },
      onError: error => {
        this.emit(contentId, { phase: 'error', error: error.message })
      },
      onShouldRetry: (_error, retryAttempt) => retryAttempt < RETRY_DELAYS.length,
    })

    entry.upload = upload

    // Resume a previous attempt for the same file if tus stored one (AC2).
    const previous = await upload.findPreviousUploads()
    if (previous.length > 0 && previous[0]) {
      upload.resumeFromPreviousUpload(previous[0])
    }
    upload.start()
  }

  pause(contentId: string): void {
    const entry = this.entries.get(contentId)
    if (!entry?.upload) return
    void entry.upload.abort()
    this.emit(contentId, { phase: 'paused' })
  }

  resume(contentId: string): void {
    const entry = this.entries.get(contentId)
    if (!entry?.upload) return
    entry.upload.start()
    this.emit(contentId, { phase: 'uploading' })
  }

  /** WD-3 — discard confirm. Aborts and forgets the resume fingerprint. */
  async discard(contentId: string): Promise<void> {
    const entry = this.entries.get(contentId)
    if (entry?.upload) {
      await entry.upload.abort(true)
    }
    this.entries.delete(contentId)
    this.emit(contentId, {
      phase: 'idle',
      fileName: '',
      bytesSent: 0,
      bytesTotal: 0,
      percent: 0,
      speed: 0,
      etaSeconds: null,
      videoId: null,
      error: null,
    })
  }

  /** True while any upload is in flight — used to warn before closing the tab. */
  hasActiveUploads(): boolean {
    for (const entry of this.entries.values()) {
      if (entry.state.phase === 'uploading' || entry.state.phase === 'creating') return true
    }
    return false
  }
}

export const uploadManager = new UploadManager()

// AC3 continues the upload across in-app navigation; a full page close still
// kills it, so warn first.
window.addEventListener('beforeunload', event => {
  if (uploadManager.hasActiveUploads()) {
    event.preventDefault()
    event.returnValue = ''
  }
})

/**
 * Ask the backend to re-run encoding for an already-uploaded video.
 * AC5: "given encoding fails … I can retry without re-uploading."
 * Returns the status `media-retry-encode` reports so the panel can render it
 * without waiting on the realtime subscription or a page reload.
 */
export async function retryEncoding(
  contentId: string,
  videoId: string,
): Promise<{ status: VideoStatus }> {
  return invokeEdgeFunction<{ status: VideoStatus }>('media-retry-encode', {
    content_id: contentId,
    video_id: videoId,
  })
}

/**
 * Short-lived signed playback URL for the in-panel preview (AC6).
 * Minted by the edge function because it needs the Bunny secret (docs/03 §5.2).
 */
export async function getAdminPreviewUrl(contentId: string): Promise<string> {
  const result = await invokeEdgeFunction<{ hls_url: string; expires_at: string }>(
    'media-playback-url',
    { content_id: contentId },
  )
  return result.hls_url
}
